import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  styled,
} from "@mui/material";
import {
  useFetchOrganizationsQuery,
  useFetchUsersWithOrganizationsQuery,
  useUpdateUserMutation,
} from "../../services/profileService";

const ROLE = {
  EMPLOYEE: "employee",
  MANAGER: "manager",
};

const safeDecodeURIComponent = (value) => {
  if (!value) return "";
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const toRoleMain = (role) => {
  if (role === ROLE.MANAGER) return ROLE.MANAGER;
  return ROLE.EMPLOYEE;
};

const sortByName = (a, b) =>
  String(a?.organization_name || "").localeCompare(String(b?.organization_name || ""), undefined, {
    sensitivity: "base",
  });

const toBoolean = (value) => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null) return false;
  const s = String(value).trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes" || s === "y") return true;
  if (s === "false" || s === "0" || s === "no" || s === "n") return false;
  return Boolean(value);
};

const EditUserOrganizations = () => {
  const { user } = useSelector((state) => state.auth);
  const { userEmail: userEmailParam } = useParams();
  const navigate = useNavigate();

  const decodedEmail = useMemo(
    () => safeDecodeURIComponent(userEmailParam).trim(),
    [userEmailParam]
  );

  const {
    data: usersPayload,
    isLoading: isUserLoading,
    isError: isUserError,
    error: userError,
  } = useFetchUsersWithOrganizationsQuery(undefined, {
    skip: user?.role !== "superadmin",
  });

  const {
    data: organizationsPayload,
    isLoading: isOrganizationsLoading,
    isError: isOrganizationsError,
    error: organizationsError,
  } = useFetchOrganizationsQuery(undefined, {
    skip: user?.role !== "superadmin",
  });

  const [updateUser, { isLoading: isSaving }] = useUpdateUserMutation();

  const users = useMemo(() => {
    const inner = usersPayload?.data;
    if (Array.isArray(inner)) return inner;
    if (Array.isArray(usersPayload)) return usersPayload;
    return [];
  }, [usersPayload]);

  const allOrganizations = useMemo(() => {
    const inner = organizationsPayload?.data;
    if (Array.isArray(inner)) return inner;
    if (Array.isArray(organizationsPayload)) return organizationsPayload;
    return [];
  }, [organizationsPayload]);

  const selectedUser = useMemo(() => {
    if (!decodedEmail) return null;
    const target = decodedEmail.toLowerCase();
    return (
      users.find((u) => String(u?.email || "").trim().toLowerCase() === target) || null
    );
  }, [users, decodedEmail]);

  const initialOrgStates = useMemo(() => {
    const orgs = selectedUser?.organizations;
    if (!Array.isArray(orgs)) return [];
    return [...orgs]
      .sort(sortByName)
      .map((o) => ({
        organization_id: o.organization_id,
        organization_name: o.organization_name,
        mainRole: toRoleMain(o.role),
        is_owner: toRoleMain(o.role) === ROLE.MANAGER ? toBoolean(o.is_owner) : false,
      }));
  }, [selectedUser]);

  const [orgStates, setOrgStates] = useState([]);
  const [addMoreOpen, setAddMoreOpen] = useState(false);
  const [selectedOrgToAdd, setSelectedOrgToAdd] = useState("");
  const [pageError, setPageError] = useState("");
  const [pageSuccess, setPageSuccess] = useState("");

  useEffect(() => {
    setOrgStates(initialOrgStates);
    setAddMoreOpen(false);
    setSelectedOrgToAdd("");
    setPageError("");
    setPageSuccess("");
  }, [initialOrgStates]);

  const usedOrgIds = useMemo(() => new Set(orgStates.map((o) => o.organization_id)), [orgStates]);

  const addableOrganizations = useMemo(() => {
    const normalized = allOrganizations
      .map((o) => ({
        organization_id: o?.organization_id ?? o?.id ?? o?.organizationID,
        organization_name: o?.organization_name ?? o?.name ?? o?.organizationName,
      }))
      .filter((o) => o.organization_id != null && o.organization_name);

    return normalized
      .filter((o) => !usedOrgIds.has(o.organization_id))
      .sort(sortByName);
  }, [allOrganizations, usedOrgIds]);

  if (user?.role !== "superadmin") {
    return <Navigate to="/view-org" replace />;
  }

  const errorMessage =
    pageError ||
    (userError && typeof userError === "object" && userError.message) ||
    (userError && typeof userError === "object" && userError.data && userError.data.message) ||
    (organizationsError &&
      typeof organizationsError === "object" &&
      organizationsError.message) ||
    (organizationsError &&
      typeof organizationsError === "object" &&
      organizationsError.data &&
      organizationsError.data.message) ||
    "Something went wrong. Please try again.";

  const handleMainRoleChange = (organization_id, newMainRole) => {
    setPageError("");
    setPageSuccess("");
    setOrgStates((prev) =>
      prev.map((o) => {
        if (o.organization_id !== organization_id) return o;
        if (newMainRole === ROLE.EMPLOYEE) {
          return { ...o, mainRole: ROLE.EMPLOYEE, is_owner: false };
        }
        return {
          ...o,
          mainRole: ROLE.MANAGER,
          is_owner: Boolean(o.is_owner),
        };
      })
    );
  };

  const handleOwnerChange = (organization_id, checked) => {
    setPageError("");
    setPageSuccess("");
    setOrgStates((prev) =>
      prev.map((o) => (o.organization_id === organization_id ? { ...o, is_owner: checked } : o))
    );
  };

  const handleAddOrganization = () => {
    setPageError("");
    setPageSuccess("");

    const orgId = Number.parseInt(String(selectedOrgToAdd), 10);
    if (!orgId || Number.isNaN(orgId)) {
      setPageError("Please select an organization.");
      return;
    }

    const org = addableOrganizations.find((o) => o.organization_id === orgId);
    if (!org) {
      setPageError("Selected organization is not available.");
      return;
    }

    setOrgStates((prev) => [
      ...prev,
      {
        organization_id: org.organization_id,
        organization_name: org.organization_name,
        mainRole: ROLE.EMPLOYEE,
        is_owner: false,
      },
    ]);
    setSelectedOrgToAdd("");
    setAddMoreOpen(false);
  };

  const onSave = async () => {
    setPageError("");
    setPageSuccess("");

    if (!selectedUser?.email) {
      setPageError("User email is missing.");
      return;
    }

    const organizations = orgStates.map((o) => {
      const role =
        o.mainRole === ROLE.EMPLOYEE
          ? ROLE.EMPLOYEE
          : ROLE.MANAGER;
      const payload = { organization_id: o.organization_id, role };
      if (role === ROLE.MANAGER) {
        payload.is_owner = Boolean(o.is_owner);
      }
      return payload;
    });

    try {
      await updateUser({
        email: selectedUser.email,
        organizations,
      }).unwrap();
      setPageSuccess("User organizations updated successfully.");
      navigate("/users");
    } catch (err) {
      const message =
        (err && typeof err === "object" && err.message) ||
        (err && typeof err === "object" && err.data && err.data.message) ||
        "Failed to update user. Please try again.";
      setPageError(String(message));
    }
  };

  if (isUserLoading || isOrganizationsLoading) {
    return (
      <SectionWrap component="section">
        <ContainerWrap>
          <ContentColumn>
            <Typography sx={{ p: 2, textAlign: "center" }}>Loading…</Typography>
          </ContentColumn>
        </ContainerWrap>
      </SectionWrap>
    );
  }

  if (isUserError || isOrganizationsError) {
    return (
      <SectionWrap component="section">
        <ContainerWrap>
          <ContentColumn>
            <Alert severity="error" sx={{ mb: 2 }}>
              {String(errorMessage)}
            </Alert>
            <CustomBtn variant="contained" onClick={() => navigate("/users")}>
              Back to users
            </CustomBtn>
          </ContentColumn>
        </ContainerWrap>
      </SectionWrap>
    );
  }

  if (!selectedUser) {
    return (
      <SectionWrap component="section">
        <ContainerWrap>
          <ContentColumn>
            <Typography sx={{ mb: 2, textAlign: "center" }}>User not found.</Typography>
            <CustomBtn variant="contained" onClick={() => navigate("/users")}>
              Back to users
            </CustomBtn>
          </ContentColumn>
        </ContainerWrap>
      </SectionWrap>
    );
  }

  return (
    <SectionWrap component="section">
      <ContainerWrap>
        <ContentColumn>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <TitleWrap component="h1">Edit user organizations</TitleWrap>
          </Box>

          {(pageError || pageSuccess) && (
            <Alert severity={pageError ? "error" : "success"} sx={{ mb: 2 }}>
              {String(pageError || pageSuccess)}
            </Alert>
          )}

          <CardWrap>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: "#2C2C2E", mb: 0.5 }} variant="body1">
                <strong>Name:</strong> {selectedUser.name ?? "—"}
              </Typography>
              <Typography sx={{ color: "#2C2C2E" }} variant="body1">
                <strong>Email:</strong> {selectedUser.email ?? "—"}
              </Typography>
            </Box>

            <TitleSmallWrap component="h5">Organizations</TitleSmallWrap>

            {orgStates.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: "center" }}>
                No organizations for this user.
              </Typography>
            ) : (
              [...orgStates].sort(sortByName).map((org) => (
                <OrgBlock key={org.organization_id ?? org.organization_name}>
                  <OrgName variant="subtitle1">{org.organization_name ?? "—"}</OrgName>

                  <RoleWrap>
                    <Typography sx={{ fontWeight: 600, mb: 1, color: "#2C2C2E" }}>
                      Role
                    </Typography>

                    <FormControl component="fieldset">
                      <RadioGroup
                        value={org.mainRole}
                        onChange={(e) =>
                          handleMainRoleChange(org.organization_id, e.target.value)
                        }
                      >
                        <FormControlLabel
                          value={ROLE.MANAGER}
                          control={<Radio />}
                          label="Manager"
                        />

                        {org.mainRole === ROLE.MANAGER && (
                          <SubRoleWrap>
                            <Box sx={{ mt: 0.5 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={Boolean(org.is_owner)}
                                    onChange={(e) =>
                                      handleOwnerChange(org.organization_id, e.target.checked)
                                    }
                                  />
                                }
                                label="Is owner"
                              />
                            </Box>
                          </SubRoleWrap>
                        )}

                        <FormControlLabel
                          value={ROLE.EMPLOYEE}
                          control={<Radio />}
                          label="Employee"
                        />
                      </RadioGroup>
                    </FormControl>
                  </RoleWrap>
                </OrgBlock>
              ))
            )}

            <Box sx={{ mt: 2 }}>
              <CustomBtn
                variant="outlined"
                sx={{ width: "100%", maxWidth: "unset", mb: addMoreOpen ? 2 : 0 }}
                onClick={() => {
                  setPageError("");
                  setPageSuccess("");
                  setAddMoreOpen((v) => !v);
                }}
                disabled={addableOrganizations.length === 0}
              >
                Add in more Organization
              </CustomBtn>

              {addMoreOpen && (
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
                  <FormControl size="small" sx={{ flex: "1 1 260px", minWidth: 240 }}>
                    <Select
                      value={selectedOrgToAdd}
                      displayEmpty
                      onChange={(e) => setSelectedOrgToAdd(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select organization</em>
                      </MenuItem>
                      {addableOrganizations.map((o) => (
                        <MenuItem key={o.organization_id} value={String(o.organization_id)}>
                          {o.organization_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <CustomBtn
                    variant="contained"
                    sx={{ flex: "0 0 auto", maxWidth: 220 }}
                    onClick={handleAddOrganization}
                  >
                    Add
                  </CustomBtn>
                </Box>
              )}
            </Box>
          </CardWrap>

          <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
            <CustomBtn
              variant="contained"
              sx={{ flex: "1 1 220px" }}
              onClick={onSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving…" : "Save"}
            </CustomBtn>
            <CustomBtn
              variant="outlined"
              sx={{ flex: "1 1 220px", background: "#1085AB", borderColor: "#1085AB" }}
              onClick={() => navigate("/users")}
              disabled={isSaving}
            >
              Back
            </CustomBtn>
          </Box>
        </ContentColumn>
      </ContainerWrap>
    </SectionWrap>
  );
};

export default EditUserOrganizations;

const SectionWrap = styled(Box)(() => ({
  padding: "30px 0",
}));

const ContainerWrap = styled(Box)(() => ({
  width: "100%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
  marginLeft: "auto",
  marginRight: "auto",
}));

const ContentColumn = styled(Box)(() => ({
  width: "100%",
  maxWidth: "720px",
  marginLeft: "auto",
  marginRight: "auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
}));

const TitleWrap = styled(Typography)(() => ({
  fontSize: "28px",
  fontWeight: 600,
  lineHeight: 1.445,
  color: "#000000",
  marginBottom: "12px",
}));

const TitleSmallWrap = styled(Typography)(() => ({
  fontSize: "20px",
  fontWeight: 600,
  lineHeight: 1.35,
  color: "#000000",
  marginTop: "18px",
  marginBottom: "12px",
}));

const CardWrap = styled(Box)(() => ({
  width: "100%",
  padding: "24px",
  background: "#ffffff",
  borderRadius: "7px",
  boxShadow: "0 0 5px 0 rgba(0, 0, 0, 0.05)",
}));

const OrgBlock = styled(Box)(() => ({
  paddingBottom: "18px",
  marginBottom: "18px",
  borderBottom: "1px solid #eeeeee",
  "&:last-of-type": {
    borderBottom: "none",
    marginBottom: 0,
    paddingBottom: 0,
  },
}));

const OrgName = styled(Typography)(() => ({
  fontWeight: 600,
  color: "#2C2C2E",
  marginBottom: "8px",
}));

const RoleWrap = styled(Box)(() => ({
  padding: "12px 14px",
  border: "1px solid rgba(0, 0, 0, 0.08)",
  borderRadius: "10px",
}));

const SubRoleWrap = styled(Box)(() => ({
  paddingLeft: "28px",
  marginTop: "-6px",
  marginBottom: "6px",
  borderLeft: "2px solid rgba(0, 0, 0, 0.08)",
}));

const CustomBtn = styled(Button)(() => ({
  width: "100%",
  minWidth: "unset",
  fontSize: "18px",
  fontWeight: 500,
  lineHeight: 1.25,
  color: "#ffffff",
  textTransform: "capitalize",
  background: "#F28021",
  borderColor: "#F28021",
  borderRadius: "12px",
  padding: "14px 24px",
  boxShadow: "none !important",
  "&:hover": {
    background: "#dc731d",
    borderColor: "#dc731d",
    boxShadow: "none !important",
  },
  "&.MuiButton-outlined": {
    background: "transparent",
    color: "#F28021",
    border: "1px solid #F28021",
    "&:hover": {
      background: "rgba(242, 128, 33, 0.08)",
      borderColor: "#dc731d",
    },
  },
  "&:disabled": {
    background: "rgba(242, 128, 33, 0.45)",
    color: "#ffffff",
    borderColor: "transparent",
  },
}));

